import { Heading, Text } from "@nbs/ui";
import { STUDY_LANDSCAPES, getPlacePath, type LandscapeId } from "../data/landscapes";
import { GuineaLandscapeMap } from "./GuineaLandscapeMap";
import { STORY_IMAGES, StoryMedia } from "./StoryMedia";
import { TerritoryHierarchy } from "./TerritoryHierarchy";

type StoryWhereStepProps = {
  readonly selectedId: LandscapeId;
  readonly onSelect: (id: LandscapeId) => void;
  readonly onBack: () => void;
  readonly onContinue: () => void;
};

export function StoryWhereStep({ selectedId, onSelect, onBack, onContinue }: StoryWhereStepProps) {
  const selected =
    STUDY_LANDSCAPES.find((place) => place.id === selectedId) ?? STUDY_LANDSCAPES[0]!;
  const path = getPlacePath(selected);

  return (
    <section
      className="nbs-story-step"
      data-testid="story-where-step"
      aria-labelledby="story-where-heading"
    >
      <div className="nbs-story-step__grid">
        <div className="nbs-story-step__copy">
          <p className="nbs-story-step__eyebrow">Étape 2 · Localisation</p>
          <Heading level={1} id="story-where-heading">
            Où sommes-nous ?
          </Heading>
          <p className="nbs-story-step__question">Quels territoires ont été étudiés ?</p>
          <Text className="nbs-story-step__lead">
            Toute l’étude se lit dans la <strong>région de Kindia</strong>, répartie entre deux
            préfectures : <strong>Kindia</strong> (Madina-Oula) et <strong>Forécariah</strong>{" "}
            (Kounounkan, Allassoya & Moussaya). Sélectionnez une localité pour voir ses zones
            d’évaluation et ses caractéristiques écologiques.
          </Text>

          <TerritoryHierarchy selectedId={selected.id} onSelect={onSelect} interactive />

          <article className="nbs-story-place-card" data-testid="story-place-detail">
            <header>
              <p className="nbs-story-place-card__path" aria-label="Chemin administratif">
                <span>{path.region}</span>
                <span aria-hidden="true">›</span>
                <span>{path.prefecture}</span>
                <span aria-hidden="true">›</span>
                <span>{path.locality}</span>
              </p>
              <Heading level={2}>{selected.label}</Heading>
            </header>

            <dl className="nbs-story-place-card__facts">
              <div>
                <dt>Type</dt>
                <dd>{selected.kind}</dd>
              </div>
              <div>
                <dt>Écologie essentielle</dt>
                <dd>{selected.ecology}</dd>
              </div>
              <div>
                <dt>Communautés</dt>
                <dd>{selected.communities}</dd>
              </div>
              <div>
                <dt>Zones d’évaluation</dt>
                <dd>
                  <ul className="nbs-story-place-card__zones">
                    {selected.zones.map((zone) => (
                      <li key={zone}>{zone}</li>
                    ))}
                  </ul>
                </dd>
              </div>
            </dl>
          </article>
        </div>

        <div className="nbs-story-step__visual">
          <StoryMedia
            src={STORY_IMAGES.landscape}
            alt="Femmes transportant la récolte, photo CECI"
            caption="Territoires habités — région de Kindia"
            ratio="card"
          />
        </div>
      </div>

      <div className="nbs-story-step__map nbs-story-step__map--band">
        <p className="nbs-story-step__map-label">Carte géographique de la Guinée</p>
        <GuineaLandscapeMap selectedId={selected.id} onSelect={onSelect} interactive />
      </div>

      <div className="nbs-story-step__actions">
        <button
          type="button"
          className="nbs-story-step__cta nbs-story-step__cta--ghost"
          onClick={onBack}
        >
          Retour : pourquoi cette étude
        </button>
        <button type="button" className="nbs-story-step__cta" onClick={onContinue}>
          Comment l’étude a‑t‑elle été réalisée ?
        </button>
      </div>
    </section>
  );
}
